import { useEffect, useState } from "react";
import api from "../../services/api";
import { styles } from "../../styles";
import { safeNumber } from "../../utils/helpers";

const TradingCalendar = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [yearData, setYearData] = useState(null);
  const [monthsData, setMonthsData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchYearData();
  }, [currentYear]);

  const fetchYearData = async () => {
    try {
      setLoading(true);

      const { data } = await api.get(`/dashboard/calendar/year?year=${currentYear}`);
      
      if (data.success && data.data) {
        // Build monthsData object
        const dataByMonth = {};
        
        data.data.months.forEach(monthData => {
           dataByMonth[monthData.month] = monthData;
        });

        setMonthsData(dataByMonth);
        setYearData({
          year: currentYear,
          yearlyTotal: data.data.yearlyTotal,
          totalPositions: data.data.totalPositions
        });
      }
    } catch (error) {
      console.error("Failed to fetch calendar data:", error);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousYear = () => {
    setCurrentYear(currentYear - 1);
  };

  const goToNextYear = () => {
    setCurrentYear(currentYear + 1);
  };

  const goToCurrentYear = () => {
    setCurrentYear(new Date().getFullYear());
  };

  const getDaysInMonth = (year, month) => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

    return { daysInMonth, startingDayOfWeek };
  };

  const getPnlForDate = (monthData, day) => {
    if (!monthData || !monthData.dailyData) return null;

    const year = currentYear;
    const month = monthData.month;
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    const dayData = monthData.dailyData.find(d => {
      const dataDate = new Date(d.date);
      const dataDateStr = `${dataDate.getFullYear()}-${String(dataDate.getMonth() + 1).padStart(2, '0')}-${String(dataDate.getDate()).padStart(2, '0')}`;
      return dataDateStr === dateStr;
    });

    return dayData;
  };

  const renderMonthCalendar = (monthNum) => {
    const monthData = monthsData[monthNum];
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentYear, monthNum);
    const days = [];

    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const today = new Date();
    const isCurrentMonth =
      monthNum === today.getMonth() + 1 &&
      currentYear === today.getFullYear();

    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          style={{
            minHeight: "20px",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
          }}
        />
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = getPnlForDate(monthData, day);
      const hasTrades = dayData && dayData.pnl !== undefined;
      const pnl = hasTrades ? dayData.pnl : 0;
      const isProfit = pnl > 0;
      const isLoss = pnl < 0;

      // Check if this is today
      const isToday = isCurrentMonth && day === today.getDate();

      days.push(
        <div
          key={day}
          style={{
            padding: "2px",
            minHeight: "20px",
            fontSize: "9px",
            border: isToday ? "1px solid #3b82f6" : "1px solid #e5e7eb",
            backgroundColor: hasTrades
              ? isProfit
                ? "#dcfce7"
                : isLoss
                ? "#fee2e2"
                : "#ffffff"
              : "#ffffff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            cursor: hasTrades ? "pointer" : "default",
          }}
          title={hasTrades ? `${dayData.positionCount} positions | ${dayData.wins}W/${dayData.losses}L | $${pnl.toFixed(2)}` : `${monthNames[monthNum - 1]} ${day}`}
        >
          <div
            style={{
              fontWeight: isToday ? "bold" : "400",
              color: "#374151",
            }}
          >
            {day}
          </div>
          {hasTrades && (
            <div
              style={{
                fontSize: "8px",
                fontWeight: "600",
                color: isProfit ? "#059669" : isLoss ? "#dc2626" : "#6b7280",
              }}
            >
              {isProfit ? "+" : ""}{Math.abs(pnl) >= 1000 ? `${(pnl/1000).toFixed(1)}k` : pnl.toFixed(0)}
            </div>
          )}
        </div>
      );
    }

    // Calculate monthly total
    const monthlyTotal = monthData ? monthData.monthlyTotal : 0;
    const totalPositions = monthData ? monthData.totalPositions : 0;

    return (
      <div
        style={{
          border: "1px solid #d1d5db",
          borderRadius: "0.375rem",
          overflow: "hidden",
          backgroundColor: "#ffffff",
        }}
      >
        {/* Month header */}
        <div
          style={{
            backgroundColor: "#f3f4f6",
            padding: "0.5rem",
            borderBottom: "1px solid #d1d5db",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#1f2937" }}>
            {monthNames[monthNum - 1]}
          </div>
          {totalPositions > 0 && (
            <div
              style={{
                fontSize: "0.75rem",
                fontWeight: "600",
                color: monthlyTotal >= 0 ? "#059669" : "#dc2626",
              }}
            >
              ${safeNumber(monthlyTotal, 0).toFixed(0)}
            </div>
          )}
        </div>

        {/* Day headers */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            backgroundColor: "#f9fafb",
          }}
        >
          {["S", "M", "T", "W", "T", "F", "S"].map((day, idx) => (
            <div
              key={idx}
              style={{
                padding: "2px",
                textAlign: "center",
                fontSize: "9px",
                fontWeight: "600",
                color: "#6b7280",
                border: "1px solid #e5e7eb",
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
          }}
        >
          {days}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ ...styles.card, padding: "2rem", textAlign: "center" }}>
        Loading calendar...
      </div>
    );
  }

  return (
    <div style={styles.card}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", margin: 0 }}>
            Trading Calendar {currentYear}
          </h2>
          {yearData && yearData.totalPositions > 0 && (
            <div
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: yearData.yearlyTotal >= 0 ? "#059669" : "#dc2626",
                padding: "0.25rem 0.75rem",
                backgroundColor: yearData.yearlyTotal >= 0 ? "#f0fdf4" : "#fef2f2",
                borderRadius: "0.375rem",
                border: `1px solid ${yearData.yearlyTotal >= 0 ? "#10b981" : "#ef4444"}`,
              }}
            >
              ${safeNumber(yearData.yearlyTotal, 0).toFixed(2)}
            </div>
          )}
        </div>

        {/* Year Navigation Controls */}
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            onClick={goToPreviousYear}
            style={{
              ...styles.button,
              ...styles.buttonSecondary,
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
            }}
          >
            ◀ {currentYear - 1}
          </button>
          <button
            onClick={goToCurrentYear}
            style={{
              ...styles.button,
              ...styles.buttonSecondary,
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
            }}
          >
            Today
          </button>
          <button
            onClick={goToNextYear}
            style={{
              ...styles.button,
              ...styles.buttonSecondary,
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
            }}
          >
            {currentYear + 1} ▶
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      {yearData && yearData.totalPositions > 0 && (
        <div
          style={{
            display: "flex",
            gap: "1.5rem",
            marginBottom: "1.5rem",
            padding: "1rem",
            backgroundColor: "#f9fafb",
            borderRadius: "0.5rem",
            fontSize: "0.875rem",
          }}
        >
          <div>
            <span style={{ color: "#6b7280" }}>Total Positions:</span>{" "}
            <span style={{ fontWeight: "600", color: "#1f2937" }}>{yearData.totalPositions}</span>
          </div>
        </div>
      )}

      {/* 12-month grid (3 columns x 4 rows) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "1rem",
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((monthNum) => (
          <div key={monthNum}>
            {renderMonthCalendar(monthNum)}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "2rem",
          marginTop: "1.5rem",
          padding: "1rem",
          backgroundColor: "#f9fafb",
          borderRadius: "0.5rem",
          fontSize: "0.875rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: "#dcfce7",
              border: "1px solid #10b981",
              borderRadius: "0.25rem",
            }}
          />
          <span style={{ color: "#6b7280" }}>Profit Day</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: "#fee2e2",
              border: "1px solid #ef4444",
              borderRadius: "0.25rem",
            }}
          />
          <span style={{ color: "#6b7280" }}>Loss Day</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: "#ffffff",
              border: "1px solid #3b82f6",
              borderRadius: "0.25rem",
            }}
          />
          <span style={{ color: "#6b7280" }}>Today</span>
        </div>
      </div>
    </div>
  );
};

export default TradingCalendar;
